/**
 * FRONTEND SECURITY SETUP
 *
 * Configures Content Security Policy and other security headers
 * based on the current environment (development vs production).
 *
 * IMPORTANT LIMITATIONS OF META TAG CSP:
 * - X-Frame-Options directive is IGNORED when delivered via meta element
 * - frame-ancestors directive is IGNORED when delivered via meta element
 * - For full security in production, these should be set at server/CDN level
 *
 * CORE Functions (what you actually call):
 * └── setupSecurityPolicies() ← Main security initialization (meta tags)
 */

import { getContentSecurityPolicy } from './csp.utils';

// =============================================================================
//  Security Configuration
// =============================================================================

// SECURITY FEATURE CONFIGURATION
export const SECURITY_CONFIG = {
  // Content Security Policy settings
  CSP_ENABLED: true,

  // Rate limiting enabled
  RATE_LIMITING_ENABLED: true,

  // Additional security features
  XSS_PROTECTION_ENABLED: true,
  CLICKJACKING_PROTECTION_ENABLED: true,

  // Environment-specific settings - production by default unless LOCAL_DEV=true
  STRICT_MODE: process.env.REACT_APP_LOCAL_DEV !== 'true',
} as const;

// =============================================================================
// 🎯 CORE FUNCTIONS - Main security setup functions
// =============================================================================

/**
 * Apply security headers via meta tags where possible
 *
 * Sets up browser security policies that can be configured via meta tags.
 * Some headers (X-Frame-Options, HSTS) require server configuration.
 */
export const setupSecurityPolicies = (): void => {
  if (!SECURITY_CONFIG.CSP_ENABLED) {
    return;
  }

  const isDevelopment = process.env.REACT_APP_LOCAL_DEV === 'true';

  // 1. Content Security Policy
  const existingCSP = document.querySelector(
    'meta[http-equiv="Content-Security-Policy"]',
  );
  if (!existingCSP) {
    const cspMeta = document.createElement('meta');
    cspMeta.setAttribute('http-equiv', 'Content-Security-Policy');
    cspMeta.setAttribute(
      'content',
      getContentSecurityPolicy(isDevelopment, true),
    );
    document.head.appendChild(cspMeta);
  }

  // 2. X-Content-Type-Options (prevents MIME sniffing)
  const existingContentType = document.querySelector(
    'meta[http-equiv="X-Content-Type-Options"]',
  );
  if (!existingContentType) {
    const contentTypeMeta = document.createElement('meta');
    contentTypeMeta.setAttribute('http-equiv', 'X-Content-Type-Options');
    contentTypeMeta.setAttribute('content', 'nosniff');
    document.head.appendChild(contentTypeMeta);
  }

  // 3. X-XSS-Protection (legacy XSS protection)
  if (SECURITY_CONFIG.XSS_PROTECTION_ENABLED) {
    const existingXSS = document.querySelector(
      'meta[http-equiv="X-XSS-Protection"]',
    );
    if (!existingXSS) {
      const xssMeta = document.createElement('meta');
      xssMeta.setAttribute('http-equiv', 'X-XSS-Protection');
      xssMeta.setAttribute('content', '1; mode=block');
      document.head.appendChild(xssMeta);
    }
  }

  // 4. Referrer Policy (controls referrer information)
  const existingReferrer = document.querySelector('meta[name="referrer"]');
  if (!existingReferrer) {
    const referrerMeta = document.createElement('meta');
    referrerMeta.setAttribute('name', 'referrer');
    referrerMeta.setAttribute('content', 'strict-origin-when-cross-origin');
    document.head.appendChild(referrerMeta);
  }
};
